import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import Header from "../components/Admin/header/Header";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import { ArrowLeft } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import {
  cancelAppointmentApi,
  getAppointmentByIdApi,
  rescheduleAppointmentApi,
} from "../services/appointment.service";
import { toast } from "react-toastify";
import Loader from "../components/Loader/loader";
import ErrorMessage from "../components/ErrorMessage/errorMessage";
import {
  DatePicker,
  renderTimeViewClock,
  TimePicker,
} from "@mui/x-date-pickers";
import dayjs from "dayjs";

const AppointmentsRescheduleCancel = () => {
  const navigate = useNavigate();
  const { id, type } = useParams();
  const isReschedule = type === "reschedule";
  const isCancel = type === "cancel";

  const [newDate, setNewDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [newTime, setNewTime] = useState(dayjs().format("HH:mm"));

  const getValidISODateTime = (date, time) => {
    if (!date || !time) return null;
    const combined = `${date}T${time}:00`;
    const parsed = new Date(combined);
    return isNaN(parsed.getTime()) ? null : parsed.toISOString();
  };

  const initialState = {
    newDateTime: getValidISODateTime(newDate, newTime),
    doctorNotes: "",
  };

  const [formData, setFormData] = useState(initialState);
  const [isLoading, setIsLoading] = useState(false);

  // Replace useGetApi with direct API call for better single object handling
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedRowLoading, setSelectedRowLoading] = useState(true);
  const [selectedRowError, setSelectedRowError] = useState(false);

  useEffect(() => {
    const fetchAppointmentData = async () => {
      if (!id) {
        setSelectedRowLoading(false);
        return;
      }
      
      setSelectedRowLoading(true);
      setSelectedRowError(false);
      
      try {
        const response = await getAppointmentByIdApi({ id });
        console.log("Reschedule page - API response:", response);
        console.log("Patient data:", response?.data?.patientId);
        
        if (response?.code === 200 || response?.success) {
          const appointmentData = response.data || response;
          console.log("Setting appointment data:", appointmentData);
          setSelectedRow(appointmentData);
          setSelectedRowError(false);
        } else {
          console.log("API response not successful:", response);
          setSelectedRowError(true);
          setSelectedRow(null);
        }
      } catch (error) {
        console.error("Error fetching appointment for reschedule:", error);
        setSelectedRowError(true);
        setSelectedRow(null);
      } finally {
        setSelectedRowLoading(false);
      }
    };

    fetchAppointmentData();
  }, [id]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const parsedValue =
      type === "checkbox" ? checked : type === "number" ? Number(value) : value;

    setFormData((preValue) => ({
      ...preValue,
      [name]: parsedValue,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let response;

    setIsLoading(true);
    if (isReschedule) {
      response = await rescheduleAppointmentApi(
        { id: selectedRow?._id },
        {
          ...formData,
          newDateTime: getValidISODateTime(newDate, newTime),
        }
      );
    } else if (isCancel) {
      response = await cancelAppointmentApi({ id: selectedRow?._id });
    }
    setIsLoading(false);

    if (response?.code === 200) {
      toast.success(
        response?.message ||
          `Appointment ${
            isReschedule ? "re-scheduled" : isCancel ? "cancelled" : null
          } successfully`
      );
      navigate("/appointments");
    } else {
      toast.error(response?.message || "Some error occurred.");
    }
  };

  useEffect(() => {
    if (!isReschedule && !isCancel) {
      navigate(-1);
    }
  }, []);

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box sx={{}}>
          <Sidebar />
        </Box>
        {selectedRowLoading ? (
          <Loader />
        ) : selectedRowError ? (
          <ErrorMessage />
        ) : (
          <Box sx={{ width: "100%", pr: "80px", py: "20px" }}>
            {/* Form */}
            <Box
              sx={{
                padding: 2,
                borderRadius: 2,
              }}
            >
              <Button
                startIcon={<ArrowLeft />}
                onClick={handleBack}
                sx={{
                  color: '#5D6A82',
                  textTransform: 'none',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  padding: 0,
                  transition: 'none !important',
                  marginBottom: '16px',
                }}
              >
                Back
              </Button>
              {/* Appointment Form */}
              <Typography
                sx={{
                  fontFamily: 'Open Sans',
                  fontWeight: 600,
                  fontSize: '20px',
                  lineHeight: '29px',
                  letterSpacing: '0px',
                  color: "#5D6A82",
                  marginBottom: "1rem",
                }}
              >
                {isReschedule ? `Reschedule Appointment` : "Cancel Appointment"}
              </Typography>

              {/* Patient Information */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4} md={3} xl={2}>
                  <Box
                    sx={{
                      width: "100%",
                      height: "15rem",
                      border: "1px solid #5d6a82",
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                      overflow: "hidden",
                      boxSizing: "border-box",
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    {selectedRow?.patientId?.patientImage ? (
                      <Box
                        component="img"
                        src={selectedRow?.patientId?.patientImage}
                        alt="Patient"
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <Typography
                        sx={{
                          color: "#999",
                          fontSize: "14px",
                          textAlign: "center",
                        }}
                      >
                        No picture
                      </Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} sm={8} md={9} xl={10}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" color="grey">
                        Patient Information
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="UHID"
                        name="UHID"
                        value={selectedRow?.patientId?.UHID || ""}
                        onChange={handleInputChange}
                        slotProps={{ input: { readOnly: true } }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Name"
                        name="name"
                        value={
                          selectedRow?.patientId?.firstName && selectedRow?.patientId?.lastName
                            ? `${selectedRow.patientId.firstName} ${selectedRow.patientId.lastName}`
                            : selectedRow?.patientId?.firstName || selectedRow?.patientId?.lastName || "-"
                        }
                        onChange={handleInputChange}
                        slotProps={{ input: { readOnly: true } }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Number"
                        name="phone"
                        value={selectedRow?.patientId?.phone || selectedRow?.patientId?.contactNumber || "-"}
                        onChange={handleInputChange}
                        slotProps={{ input: { readOnly: true } }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Gender"
                        name="gender"
                        value={selectedRow?.patientId?.gender || ""}
                        onChange={handleInputChange}
                        slotProps={{ input: { readOnly: true } }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Address"
                        name="address"
                        value={selectedRow?.patientId?.address || ""}
                        onChange={handleInputChange}
                        slotProps={{ input: { readOnly: true } }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="City"
                        name="city"
                        value={selectedRow?.patientId?.city?.cityName || ""}
                        onChange={handleInputChange}
                        slotProps={{ input: { readOnly: true } }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="State"
                        name="state"
                        value={selectedRow?.patientId?.state?.stateName || ""}
                        onChange={handleInputChange}
                        slotProps={{ input: { readOnly: true } }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Pincode"
                        name="pinCode"
                        value={selectedRow?.patientId?.pinCode || ""}
                        onChange={handleInputChange}
                        slotProps={{ input: { readOnly: true } }}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              {/* Appointment Information */}
              <Box
                sx={{
                  px: 2,
                  py: 3,
                  backgroundColor: !type ? "#f5faff" : "#fff",
                  border: "1px solid grey",
                  borderRadius: 2,
                  my: 3,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 2,
                  }}
                >
                  <Typography variant="subtitle1" color="grey">
                    Appointment Information
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <TextField
                      label="Unit"
                      value={selectedRow?.unit?.unitName || ""}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <TextField
                      label="Doctor Name"
                      value={`${selectedRow?.doctorName?.firstName || ""} ${
                        selectedRow?.doctorName?.lastName || ""
                      }`}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <DatePicker
                      label="Date"
                      slotProps={{ textField: { fullWidth: true } }}
                      value={
                        selectedRow?.appointmentDateTime
                          ? dayjs(selectedRow.appointmentDateTime)
                          : null
                      }
                      readOnly
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <TimePicker
                      ampm={false}
                      label="Time"
                      slotProps={{ textField: { fullWidth: true } }}
                      value={
                        selectedRow?.appointmentDateTime
                          ? dayjs(selectedRow?.appointmentDateTime, "HH:mm")
                          : null
                      }
                      readOnly
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Reschedule Information */}
              {isReschedule ? (
                <Box
                  sx={{
                    borderRadius: 2,
                    px: 2,
                    py: 3,
                    mb: 3,
                    bgcolor: "#F0F7FC",
                  }}
                >
                  {/* Appointment Form */}
                  <Typography sx={{ mb: 2, color: "#5D6A82" }}>
                    Reschedule Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                      <DatePicker
                        label="New Date"
                        disablePast
                        slotProps={{ textField: { fullWidth: true } }}
                        value={newDate ? dayjs(newDate) : null}
                        onChange={(newValue) =>
                          setNewDate(
                            newValue
                              ? dayjs(newValue).format("YYYY-MM-DD")
                              : null
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                      <TimePicker
                        label="New Time"
                        slotProps={{ textField: { fullWidth: true } }}
                        value={newTime ? dayjs(newTime, "HH:mm") : null}
                        onChange={(newValue) =>
                          setNewTime(
                            newValue ? dayjs(newValue).format("HH:mm") : null
                          )
                        }
                        viewRenderers={{
                          hours: renderTimeViewClock,
                          minutes: renderTimeViewClock,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4} lg={6}>
                      <TextField
                        label="Doctor's Notes/Instruction"
                        name="doctorNotes"
                        value={formData?.doctorNotes || ""}
                        onChange={handleInputChange}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </Box>
              ) : // <FormControlLabel
              //   control={<Checkbox defaultChecked />}
              //   label="Initiate Fund"
              // />
              null}

              {/* Submit Button */}
              <Box sx={{ textAlign: "center" }}>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ padding: "10px 40px", textTransform: "none" }}
                  onClick={handleSubmit}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <CircularProgress size={24} />
                  ) : isReschedule ? (
                    `Save Changes`
                  ) : (
                    "Cancel Appointment"
                  )}
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default AppointmentsRescheduleCancel;
